import { NavigationActions, NavigationApiActions } from '@common/store/navigation/actions/navigation.actions';
import {
    NavigationGroup,
    NavigationGroupTypes,
    NavigationItem,
    NavigationItemTypes,
    NavigationSection,
    NavigationSectionTypes,
    NavigationState,
} from '@common/store/navigation/models/navigation.models';
import { createReducer, on } from '@ngrx/store';
import { RecordFactory } from '@src/suite/common/data-models/records/record-factory';
import { SearchViewStateRecord } from '@src/suite/common/data-models/records/search-viewstate-record';
import { SingleItemViewStateRecord } from '@src/suite/common/data-models/records/single-item-viewstate-record';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { ViewStateTypesEnum } from '@src/suite/common/enums/viewstate-types';

export const initialState: NavigationState = {
    application: null,
    group: null,
    section: null,
    item: null,
    default: null,
    navigation: {
        cast: new Array<NavigationGroup>(),
        discover: new Array<NavigationGroup>(),
        journal: new Array<NavigationGroup>(),
        playbook: new Array<NavigationGroup>(),
        live: new Array<NavigationGroup>(),
    },
};

function handleAddNavigationItem(state: NavigationState, grp: NavigationGroup, sec: NavigationSection, itm: NavigationItem): NavigationState {
    let group: NavigationGroup = null;
    let section: NavigationSection = null;
    let item: NavigationItem = null;

    state.navigation[state.application] = state.navigation[state.application].map((g: NavigationGroup) => {
        if (g.id === grp.id) {
            group = g;
            return {
                ...g,
                items: [
                    ...g.items.map((s: NavigationSection) => {
                        if (s.id === sec.id) {
                            section = s;
                            if (s.id === NavigationSectionTypes.System) {
                                s.items = s.items.map(i => {
                                    if (i.guid === itm.guid) {
                                        item = itm;
                                        return { ...itm };
                                    }
                                    return i;
                                });
                            } else {
                                itm.order = s.items.length;
                                item = itm;
                                s.items = [...s.items, itm];
                            }
                            return s;
                        } else {
                            return s;
                        }
                    }),
                ],
            };
        } else {
            return g;
        }
    });

    if (group && section && item) {
        return { ...state, group: { ...group }, section: { ...section }, item: { ...item } };
    }
    return state;
}

function handleSetNavigationItem(state: NavigationState, grp: string, sec: string, itm: string): NavigationState {
    let group: NavigationGroup = null;
    let section: NavigationSection = null;
    let item: NavigationItem = null;

    if (grp && sec && itm) {
        state.navigation[state.application] = state.navigation[state.application].map((g: NavigationGroup) => {
            if (g.path === grp) {
                group = g;
                g.items = [
                    ...g.items.map((s: NavigationSection) => {
                        if ((g.path !== 'system' && s.path === sec) || (g.path === 'system' && s.path === 'system')) {
                            section = s;
                            item = s.items.find((i: NavigationItem) => i.guid === itm);
                            return { ...s, opened: true };
                        } else {
                            return s;
                        }
                    }),
                ];
                return { ...g, opened: true };
            } else {
                return g;
            }
        });
        if (item && item !== state.item) {
            if (item.viewState) {
                const newViewState = RecordFactory.createViewStateForType(item.viewState.viewStateType);
                item.viewState.copyDataTo(newViewState, true);
                return { ...state, group: { ...group }, section: { ...section }, item: { ...item, viewState: newViewState } };
            }
        } else {
            return state;
        }
    } else {
        return { ...state, group: null, section: null, item: null };
    }
}

function handleUpdateSingleItemNavigationItemViewState(item: NavigationItem, guid?: string, itemtype?: ObjectTypes, title?: string): NavigationItem {
    const vs = <SingleItemViewStateRecord>RecordFactory.createViewStateForType(item.viewState.viewStateType);
    item.viewState.copyDataTo(vs, true);
    if (guid) {
        item = { ...item, guid: guid };
        vs.itemGuid = guid;
    }
    if (itemtype) {
        vs.itemType = itemtype;
    }
    if (title) {
        vs.title = title;
    }
    return { ...item, label: vs.title, viewState: vs };
}

function handleUpdateSingleNavigationItem(state: NavigationState, guid?: string, itemtype?: ObjectTypes, title?: string): NavigationState {
    let item: NavigationItem = null;

    const navigation = {
        ...state.navigation,
        [state.application]: state.navigation[state.application].map((g: NavigationGroup) => {
            if (g.id === NavigationGroupTypes.System) {
                return {
                    ...g,
                    items: g.items.map((s: NavigationSection) => {
                        if (s.id === NavigationSectionTypes.System) {
                            return {
                                ...s,
                                items: s.items.map((i: NavigationItem) => {
                                    if (i && i.viewState && i.viewState.viewStateType === ViewStateTypesEnum.SINGLEITEM) {
                                        item = handleUpdateSingleItemNavigationItemViewState(i, guid, itemtype, title);
                                        return item;
                                    } else {
                                        return i;
                                    }
                                }),
                            };
                        } else {
                            return s;
                        }
                    }),
                };
            } else {
                return g;
            }
        }),
    };

    return { ...state, item: item, navigation: navigation };
}

function findNavigationItem(state: NavigationState, group: NavigationGroup, section: NavigationSection, guid: string): NavigationItem {
    let item: NavigationItem;
    exit: for (const grp of state.navigation[state.application]) {
        if (grp.id === group.id) {
            for (const sec of grp.items) {
                if (sec.id === section.id) {
                    item = sec.items.find((itm: NavigationItem) => itm.guid === guid);
                    break exit;
                }
            }
        }
    }
    return item;
}

export const navigationReducer = createReducer(
    initialState,
    on(NavigationActions.clearNavigationData, (): NavigationState => {
        return {
            application: null,
            group: null,
            section: null,
            item: null,
            default: null,
            navigation: {
                cast: new Array<NavigationGroup>(),
                discover: new Array<NavigationGroup>(),
                journal: new Array<NavigationGroup>(),
                playbook: new Array<NavigationGroup>(),
                live: new Array<NavigationGroup>(),
            },
        };
    }),
    on(NavigationActions.loadNavigationDataSuccess, (_, action): NavigationState => {
        return { ...action.state };
    }),
    on(NavigationActions.initializeNavigationDataSuccess, (_, action): NavigationState => {
        return { ...action.state };
    }),
    on(NavigationApiActions.loadTeamNavigationData, state => {
        return {
            ...state,
            navigation: {
                ...state.navigation,
                [state.application]: state.navigation[state.application].map((group: NavigationGroup) => {
                    if (group.id === NavigationGroupTypes.Team) {
                        return { ...group, items: [] };
                    } else {
                        return group;
                    }
                }),
            },
        };
    }),
    on(NavigationActions.initializeTeamNavigationDataSuccess, (state, action) => {
        return {
            ...state,
            navigation: {
                ...state.navigation,
                [state.application]: state.navigation[state.application].map((group: NavigationGroup) => {
                    if (group.id === NavigationGroupTypes.Team) {
                        return { ...group, items: action.sections };
                    } else {
                        return group;
                    }
                }),
            },
        };
    }),
    on(NavigationActions.setApplication, (state, action): NavigationState => {
        if (action.application === state.application) return state;

        let guid: string = null;
        let item: NavigationItem = null;
        state.navigation[action.application]
            .sort((a: NavigationGroup, b: NavigationGroup) => {
                if (a.id < b.id) return -1;
                if (a.id > b.id) return 1;
                return 0;
            })
            .forEach((g: NavigationGroup) => {
                if (g.id > 0 && g.default) guid = g.default;
            });

        state.navigation[action.application]
            .sort((a: NavigationGroup, b: NavigationGroup) => {
                if (a.id < b.id) return -1;
                if (a.id > b.id) return 1;
                return 0;
            })
            .forEach((g: NavigationGroup) => {
                if (g.id > 0) {
                    g.items.forEach((s: NavigationSection) => {
                        s.items.forEach((i: NavigationItem) => {
                            // First or default navigation item
                            if (!item || (guid && i.viewState?.guid === guid)) item = i;
                        });
                    });
                }
            });

        return {
            ...state,
            item: null,
            default: item,
            ['application']: action.application,
        };
    }),
    on(NavigationActions.updateGroup, (state, action) => {
        state.navigation[state.application] = state.navigation[state.application].map((item: NavigationGroup) => {
            if (item.id === action.group.id) {
                return { ...action.group };
            } else {
                return item;
            }
        });
        return state;
    }),
    on(NavigationActions.updateGroupState, (state, action) => {
        state.navigation[state.application] = state.navigation[state.application].map((group: NavigationGroup) => {
            if (group.id === action.group.id) {
                return { ...group, opened: action.group.opened };
            } else {
                return group;
            }
        });
        return state;
    }),
    on(NavigationActions.addSection, (state, action) => {
        return {
            ...state,
            navigation: {
                ...state.navigation,
                [state.application]: [
                    ...state.navigation[state.application].map(grp => {
                        if (grp.id === action.group.id && grp.items.findIndex(item => item.id === action.section.id) === -1) {
                            return { ...grp, items: [...grp.items, action.section] };
                        } else {
                            return grp;
                        }
                    }),
                ],
            },
        };
    }),
    on(NavigationActions.removeSection, (state, action) => {
        return {
            ...state,
            navigation: {
                ...state.navigation,
                [state.application]: [
                    ...state.navigation[state.application].map((group: NavigationGroup) => {
                        if (group.id === action.group.id) {
                            return { ...group, items: group.items.filter(section => section.id !== action.section.id) };
                        }
                        return group;
                    }),
                ],
            },
        };
    }),
    on(NavigationActions.updateSection, (state, action) => {
        return {
            ...state,
            navigation: {
                ...state.navigation,
                [state.application]: [
                    ...state.navigation[state.application].map((group: NavigationGroup) => {
                        if (group.id === action.group.id) {
                            return {
                                ...group,
                                items: group.items.map((section: NavigationSection) => {
                                    if (section.id === action.section.id) {
                                        return { ...action.section };
                                    } else {
                                        return section;
                                    }
                                }),
                            };
                        } else {
                            return group;
                        }
                    }),
                ],
            },
        };
    }),
    on(NavigationActions.updateSectionState, (state, action) => {
        state.navigation[state.application] = state.navigation[state.application].map((grp: NavigationGroup) => {
            if (grp.id === action.group.id) {
                grp.items = [
                    ...grp.items.map((sc: NavigationSection) => {
                        if (sc.id === action.section.id) {
                            return { ...sc, opened: action.section.opened };
                        } else {
                            return sc;
                        }
                    }),
                ];
            }
            return grp;
        });
        return state;
    }),
    on(NavigationActions.addItem, (state, action) => {
        return {
            ...state,
            navigation: {
                ...state.navigation,
                [state.application]: [
                    ...state.navigation[state.application].map(grp => {
                        return {
                            ...grp,
                            items: [
                                ...grp.items.map(sec => {
                                    if (sec.id === action.section.id) {
                                        if (sec.items.findIndex(itm => itm.guid === action.item.guid) > -1) {
                                            return sec;
                                        } else {
                                            return {
                                                ...sec,
                                                items: [...sec.items, action.item],
                                            };
                                        }
                                    } else {
                                        return sec;
                                    }
                                }),
                            ],
                        };
                    }),
                ],
            },
        };
    }),
    on(NavigationActions.removeItem, (state, action) => {
        return {
            ...state,
            navigation: {
                ...state.navigation,
                [state.application]: [
                    ...state.navigation[state.application].map((group: NavigationGroup) => {
                        if (group.id === action.group.id) {
                            return {
                                ...group,
                                items: group.items.map((section: NavigationSection) => {
                                    if (section.id === action.section.id) {
                                        return {
                                            ...section,
                                            items: section.items.filter(item => {
                                                return item.guid !== action.item.guid;
                                            }),
                                        };
                                    }
                                    return section;
                                }),
                            };
                        }
                        return group;
                    }),
                ],
            },
        };
    }),
    on(NavigationActions.removeItemFromGroups, (state, action) => {
        return {
            ...state,
            navigation: {
                ...state.navigation,
                [state.application]: [
                    ...state.navigation[state.application].map((group: NavigationGroup) => {
                        if (action.groups.indexOf(group.id) > -1) {
                            return {
                                ...group,
                                items: group.items.map((section: NavigationSection) => {
                                    return {
                                        ...section,
                                        items: section.items.filter(item => {
                                            return item.viewState.guid !== action.vs.guid;
                                        }),
                                    };
                                }),
                            };
                        }
                        return group;
                    }),
                ],
            },
        };
    }),
    on(NavigationActions.setCurrentItem, (state, action) => {
        return handleSetNavigationItem(state, action.group, action.section, action.item);
    }),
    on(NavigationApiActions.saveCurrentNavigationItemSuccess, (state, _): NavigationState => {
        return {
            ...state,
            navigation: {
                ...state.navigation,
                [state.application]: [
                    ...state.navigation[state.application].map((group: NavigationGroup) => {
                        return {
                            ...group,
                            items: group.items.map((section: NavigationSection) => {
                                return {
                                    ...section,
                                    items: section.items.map((item: NavigationItem) => {
                                        if (item && item.viewState && item.viewState.guid === state.item.viewState.guid) {
                                            const newViewState = RecordFactory.createViewStateForType(state.item.viewState.viewStateType);
                                            state.item.viewState.copyDataTo(newViewState, true);
                                            return {
                                                ...item,
                                                viewState: newViewState,
                                            };
                                        }
                                        return item;
                                    }),
                                };
                            }),
                        };
                    }),
                ],
            },
        };
    }),
    on(NavigationApiActions.updateNavigationItemSuccess, (state, action): NavigationState => {
        let current = state.item;
        if (state.item && state.item.viewState && state.item.viewState.guid === action.vs.guid) {
            current = {
                ...state.item,
                label: action.vs.title,
                viewState: action.vs,
            };
        }
        return {
            ...state,
            item: current,
            navigation: {
                ...state.navigation,
                [state.application]: [
                    ...state.navigation[state.application].map((group: NavigationGroup) => {
                        return {
                            ...group,
                            items: group.items.map((section: NavigationSection) => {
                                return {
                                    ...section,
                                    items: section.items.map((item: NavigationItem) => {
                                        if (item && item.viewState && item.viewState.guid === action.vs.guid) {
                                            return {
                                                ...item,
                                                label: action.vs.title,
                                                viewState: action.vs,
                                            };
                                        }
                                        return item;
                                    }),
                                };
                            }),
                        };
                    }),
                ],
            },
        };
    }),
    on(NavigationApiActions.createNavigationItemSuccess, (state, action) => {
        return handleAddNavigationItem(state, action.group, action.section, action.item);
    }),
    on(NavigationApiActions.addNavigationItemSuccess, (state, action) => {
        return handleAddNavigationItem(state, action.group, action.section, action.item);
    }),
    on(NavigationActions.enterSearchMode, (state, action) => {
        let group: NavigationGroup = null;
        let section: NavigationSection = null;
        let item: NavigationItem = null;

        state.navigation[state.application] = state.navigation[state.application].map((g: NavigationGroup) => {
            if (g.id === NavigationGroupTypes.System) {
                group = g;
                g.items = [
                    ...g.items.map((s: NavigationSection) => {
                        if (s.id === NavigationSectionTypes.System) {
                            section = s;
                            item = s.items.find((i: NavigationItem) => i.guid === NavigationItemTypes.SYSTEM_SEARCH_GUID);
                            return { ...s, opened: true };
                        } else {
                            return s;
                        }
                    }),
                ];
                return { ...g, opened: true };
            } else {
                return g;
            }
        });
        if (item && item !== state.item && item.viewState && item.viewState instanceof SearchViewStateRecord) {
            item.viewState.viewStateSearchQuery = action.term;
            item.viewState.title = `Search - ${action.term}`;
            return { ...state, group: { ...group }, section: { ...section }, item: { ...item } };
        }

        return state;
    }),
    on(NavigationActions.loadSingleNavigationItem, (state, action) => {
        return handleUpdateSingleNavigationItem(state, action.guid, null);
    }),
    on(NavigationActions.setSingleNavigationItemData, (state, action) => {
        return handleUpdateSingleNavigationItem(state, null, action.itemtype, action.title);
    }),
    on(NavigationActions.loadNavigationItem, (state, action) => {
        let group: string = null;
        let section: string = null;
        let item: string = null;

        state.navigation[state.application].forEach((g: NavigationGroup) => {
            g.items.forEach((s: NavigationSection) => {
                s.items.forEach((i: NavigationItem) => {
                    if (i && i.viewState && i.viewState.guid === action.vs.guid) {
                        group = g.path;
                        section = s.path;
                        item = i.viewState.guid;
                    }
                });
            });
        });

        if (!item) {
            state = {
                ...state,
                navigation: {
                    ...state.navigation,
                    [state.application]: state.navigation[state.application].map((g: NavigationGroup) => {
                        if (g.id === NavigationGroupTypes.System) {
                            return {
                                ...g,
                                items: g.items.map((s: NavigationSection) => {
                                    if (s.id === NavigationSectionTypes.System) {
                                        return {
                                            ...s,
                                            items: s.items.map((i: NavigationItem) => {
                                                if (i.guid === NavigationItemTypes.SYSTEM_CURRENT_VIEW_GUID) {
                                                    group = g.path;
                                                    section = s.path;
                                                    item = NavigationItemTypes.SYSTEM_CURRENT_VIEW_GUID;
                                                    return { ...i, viewState: action.vs };
                                                } else {
                                                    return i;
                                                }
                                            }),
                                        };
                                    } else {
                                        return s;
                                    }
                                }),
                            };
                        } else {
                            return g;
                        }
                    }),
                },
            };
        }

        return handleSetNavigationItem(state, group, section, item);
    }),
    on(NavigationActions.reorderItems, (state, action): NavigationState => {
        const item = findNavigationItem(state, action.group, action.sourceSection, action.guid);
        if (item) {
            return {
                ...state,
                navigation: {
                    ...state.navigation,
                    [state.application]: state.navigation[state.application].map((g: NavigationGroup) => {
                        if (g.id === action.group.id) {
                            return {
                                ...g,
                                items: g.items.map((s: NavigationSection) => {
                                    if (action.sourceSection.id === action.targetSection.id && action.targetSection.id === s.id) {
                                        // We are draggin and dropping within the same section
                                        s.items.splice(action.sourceIndex, 1);
                                        s.items.splice(action.targetIndex, 0, item);
                                        return {
                                            ...s,
                                        };
                                    } else {
                                        if (s.id === action.sourceSection.id) {
                                            s.items.splice(action.sourceIndex, 1);
                                            return {
                                                ...s,
                                            };
                                        }
                                        if (s.id === action.targetSection.id) {
                                            s.items.splice(action.targetIndex, 0, item);
                                            return {
                                                ...s,
                                            };
                                        }
                                        return s;
                                    }
                                }),
                            };
                        }
                        return g;
                    }),
                },
            };
        }
        return state;
    }),
    on(NavigationApiActions.setDefaultNavigationItemSuccess, (state, action): NavigationState => {
        return {
            ...state,
            navigation: {
                ...state.navigation,
                [state.application]: state.navigation[state.application].map((group: NavigationGroup) => {
                    if (group.id === action.group) {
                        return {
                            ...group,
                            default: action.guid,
                        };
                    } else {
                        return group;
                    }
                }),
            },
        };
        return state;
    })
);
